/**
 * OnboardingPage Component
 *
 * Protected page that hosts the onboarding wizard.
 * Requires authentication but NOT a completed profile or subscription.
 * Redirects to dashboard if onboarding is already complete.
 */

import { Navigate } from "react-router-dom";
import { HeartHandshake } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useOnboarding } from "../hooks/useOnboarding";
import { OnboardingWizard } from "../components/onboarding/OnboardingWizard";
import { LoadingSpinner } from "../components/LoadingSpinner";

export function OnboardingPage() {
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
  } = useAuth();
  const { isOnboardingComplete, isLoading: onboardingLoading } =
    useOnboarding();

  if (authLoading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    loginWithRedirect();
    return <LoadingSpinner message="Redirecting to login..." />;
  }

  if (onboardingLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // If onboarding is already complete, redirect to dashboard
  if (isOnboardingComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <HeartHandshake className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold text-foreground">
              AttuneWell
            </span>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-12 max-w-lg">
        <OnboardingWizard />
      </main>
    </div>
  );
}
